import clsx from 'clsx';
import { DatePicker } from 'components/Form/DatePicker/DatePicker';
import { ImageInput } from 'components/Form/ImageInput/ImageInput';
import { Input } from 'components/Form/Input/Input';
import { RadioGroup } from 'components/Form/RadioGroup/RadioGroup';
import { SelectAutocomplete } from 'components/Form/SelectAutocomplete/SelectAutocomplete';
import { PetVaccinesForm } from 'components/Pet/Form/Vaccines/PetVaccinesForm';
import { Button } from 'components/UI/Button/Button';
import { usePetFormData } from 'hooks/usePetFormData';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { IntakeFormCustomQuestion } from 'types/IntakeForm';
import { emptySavePet, Pet, PetTypeIds, SavePet } from 'types/Pet';
import { getPetSizeByWeight } from 'utils/petUtils';

import classes from './PetForm.module.scss';

interface PetFormProps {
    pet?: Pet;
    loading?: boolean;
    showSubmitButton?: boolean;
    submitButtonLabel?: string;
    questions?: Array<IntakeFormCustomQuestion>;
    className?: string;
    onChange?: (isValid: boolean) => void;
    onSubmit?: (pet: Pet) => void;
}

export const PetForm = React.forwardRef<{ submit: () => void }, PetFormProps>(
    (
        {
            pet,
            loading,
            showSubmitButton,
            submitButtonLabel,
            questions,
            className,
            onChange,
            onSubmit
        },
        ref
    ) => {
        const {
            watch,
            control,
            register,
            setValue,
            handleSubmit,
            formState: { errors, isValid }
        } = useForm<SavePet>({
            defaultValues: pet ?? emptySavePet(),
            mode: 'onChange'
        });

        const formValues = watch();

        const { types, sizes, hairLengths, breeds, genders, hairColors } = usePetFormData(
            formValues.type?.id
        );

        const formRef = React.useRef<HTMLFormElement>(null);

        React.useEffect(() => {
            onChange?.(isValid);
            // eslint-disable-next-line
        }, [isValid]);

        React.useEffect(() => {
            const subscription = watch((value, { name }) => {
                if (['weight', 'type'].includes(String(name)) && value.type?.id) {
                    setValue(
                        'size',
                        getPetSizeByWeight(Number(value.weight), value.type?.id, sizes)
                    );
                }

                if (name === 'type') {
                    setValue('breed', null);

                    if (value.type?.id === PetTypeIds.EXOTIC) {
                        setValue('hairLength', null);
                    }
                }
            });

            return () => subscription.unsubscribe();
            // eslint-disable-next-line
        }, [watch, sizes]);

        React.useImperativeHandle(ref, () => ({
            submit: () => {
                handleSubmit(submitHandler)();
            }
        }));

        const questionChangeHandler = (
            index: number,
            question: IntakeFormCustomQuestion,
            value: string
        ) => {
            setValue(`answers.${index}`, {
                question: question.question,
                questionId: question.id,
                answer: value
            });
        };

        const submitHandler = (data: SavePet) => {
            onSubmit?.(data as Pet);
        };

        return (
            <form
                ref={formRef}
                className={clsx(classes.root, className)}
                onSubmit={handleSubmit(submitHandler)}
            >
                <div className={classes.container}>
                    <Controller
                        name="image"
                        control={control}
                        render={({ field }) => (
                            <ImageInput
                                id="image"
                                value={field.value?.src}
                                label="Upload a photo of your pet"
                                className={classes.imageInput}
                                onChange={(file, src) => field.onChange({ file, src })}
                            />
                        )}
                    />

                    <div className="w-full">
                        <Controller
                            name="type"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <RadioGroup
                                    label="Pet type"
                                    options={types}
                                    value={field.value?.id}
                                    className={classes.radioGroup}
                                    onChange={value =>
                                        field.onChange({
                                            id: value.id,
                                            singular: value.name,
                                            plural: value.description ?? ''
                                        })
                                    }
                                />
                            )}
                        />

                        {formValues.type?.id && (
                            <>
                                <Input
                                    required
                                    label="Pet name"
                                    error={!!errors.name}
                                    {...register('name', { required: true })}
                                />
                                <div className={classes.grid}>
                                    <Controller
                                        name="breed"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field }) => (
                                            <SelectAutocomplete
                                                required
                                                label={
                                                    formValues.type?.id === PetTypeIds.EXOTIC
                                                        ? 'Specie'
                                                        : 'Breed'
                                                }
                                                options={breeds}
                                                value={field.value}
                                                error={!!errors.breed}
                                                onChange={field.onChange}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="weight"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                required
                                                label="Weight"
                                                inputMode="numeric"
                                                error={!!errors.weight}
                                                onChange={field.onChange}
                                            />
                                        )}
                                    />
                                </div>
                                <div className={classes.grid}>
                                    <Controller
                                        name="gender"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field }) => (
                                            <SelectAutocomplete
                                                required
                                                label="Gender"
                                                options={genders}
                                                value={field.value}
                                                error={!!errors.gender}
                                                onChange={field.onChange}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="hairLength"
                                        control={control}
                                        rules={{
                                            validate: value => {
                                                if (formValues.type?.id !== 'exotics' && !value) {
                                                    return 'Hair length is required';
                                                }
                                                return true;
                                            }
                                        }}
                                        render={({ field }) => (
                                            <SelectAutocomplete
                                                label="Hair Length"
                                                value={field.value}
                                                error={!!errors.hairLength}
                                                required={formValues.type?.id !== 'exotics'}
                                                options={hairLengths}
                                                onChange={field.onChange}
                                            />
                                        )}
                                    />
                                </div>
                                <div className={classes.grid}>
                                    <Controller
                                        name="birthdate"
                                        control={control}
                                        render={({ field }) => (
                                            <DatePicker
                                                required
                                                label="Birthday"
                                                value={field.value}
                                                onChange={field.onChange}
                                            />
                                        )}
                                    />

                                    <Controller
                                        name="hairColor"
                                        control={control}
                                        render={({ field }) => (
                                            <SelectAutocomplete
                                                label="Color"
                                                value={field.value}
                                                options={hairColors}
                                                onChange={field.onChange}
                                            />
                                        )}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    {formValues.type?.id && (
                        <Controller
                            name="vaccines"
                            control={control}
                            render={({ field }) => (
                                <PetVaccinesForm
                                    petVaccines={pet?.vaccines ?? []}
                                    onChange={field.onChange}
                                />
                            )}
                            rules={{
                                validate: value => {
                                    if (value?.some(v => !v.expiration)) {
                                        return 'Expiration date is required';
                                    }
                                    return true;
                                }
                            }}
                        />
                    )}

                    {formValues.type?.id && questions && (
                        <div className="flex flex-col gap-5">
                            {questions.map((question, index) => (
                                <Input
                                    multiline
                                    key={question.id}
                                    label={question.question}
                                    required={question.isRequired}
                                    placeholder="Write your answer"
                                    {...register(`answers.${index}.answer`, {
                                        required: question.isRequired,
                                        onChange: e =>
                                            questionChangeHandler(index, question, e.target.value)
                                    })}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {showSubmitButton && (
                    <div className="w-full">
                        <Button
                            type="submit"
                            variant="black"
                            loading={loading}
                            disabled={!isValid}
                            onClick={handleSubmit(submitHandler)}
                        >
                            {submitButtonLabel ?? 'Submit'}
                        </Button>
                    </div>
                )}
            </form>
        );
    }
);

PetForm.displayName = 'PetForm';
